import { PatientLayout } from "@/components/layout/patient-layout";
import { useState, useMemo } from "react";
import { Search, ChevronDown, ChevronRight, MessageCircle, FileText } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Link } from "wouter";

const FAQ_ITEMS = [
  {
    q: "Can I have the flu jab / COVID vaccine while on my medication?",
    a: "Most people with rheumatic conditions can and should have the flu jab and COVID vaccines. Some medications (like rituximab and methotrexate) may reduce vaccine effectiveness, so timing matters. In general, it is safe to have the vaccine — you do not need to stop your medication unless your rheumatology team advises otherwise. If you have any concerns, speak to your rheumatology nurse.",
  },
  {
    q: "Should I stop my medication before surgery or a dental procedure?",
    a: "Some rheumatology medications (especially methotrexate and biologics) may need to be paused before surgery to reduce infection risk and help healing. You should discuss this with your rheumatology team and your surgeon — do not stop medication on your own. Generally, the recommendation depends on the type of surgery and the specific drug.",
  },
  {
    q: "I'm having side effects from my medication — what should I do?",
    a: "Contact your rheumatology advice line or your GP. Do not stop taking your medication without speaking to a healthcare professional first, as some medications need to be stopped gradually. If you have serious symptoms like difficulty breathing, swelling of the face or throat, a severe rash, or signs of an allergic reaction, call 999 immediately.",
  },
  {
    q: "When will my blood test results be ready — and how do I get them?",
    a: "Blood test results are usually available within a few days (longer if they are being sent to your rheumatology team from your GP). If you had blood tests more than a week ago and have not heard back, contact your GP surgery first. If the tests were specifically for your rheumatology medication monitoring, contact your rheumatology advice line.",
  },
  {
    q: "How do I get a repeat prescription?",
    a: "Use the Prescription Query option on the RheumHub Advice Line. You will need to tell us whether your medication is a tablet or injection, and whether you have had recent blood tests. Your rheumatology team will review your request. If you usually get your medication from your GP or a homecare delivery service, they may handle the prescription directly — check with your local pharmacy.",
  },
  {
    q: "I'm feeling more pain / stiffness than usual — is this a flare?",
    a: "It could be a flare, but it could also be caused by other factors like stress, infection, overactivity, or changes in your medication. Use the Flare Assessment tool on RheumHub (available from the AI Triage feature) to help decide whether this is a flare and what to do next. If you have severe symptoms or are struggling to manage, contact your rheumatology advice line by phone.",
  },
  {
    q: "Can I take over-the-counter painkillers with my rheumatology medication?",
    a: "Some over-the-counter painkillers (especially ibuprofen, naproxen, and other NSAIDs) can interact with rheumatology medications. Paracetamol is generally safe to take with most rheumatology drugs, but check with your pharmacist or rheumatology nurse if you are unsure. Always tell your pharmacist about all the medications you are taking.",
  },
  {
    q: "Do I need to be careful in the sun while on my medication?",
    a: "Yes — some rheumatology medications (especially hydroxychloroquine and certain immunosuppressants) can make your skin more sensitive to sunlight. Use high-factor sun protection (SPF 50), wear protective clothing, and avoid prolonged sun exposure. This is especially important in summer or if you are travelling to a sunny destination.",
  },
  {
    q: "Can I drive while taking my medication?",
    a: "Most rheumatology medications do not affect your ability to drive. However, some medications can cause drowsiness, dizziness, or blurred vision — especially when you first start them. If you feel drowsy or dizzy, do not drive. You have a legal obligation to tell the DVLA about any medical condition that may affect your driving. If in doubt, ask your rheumatology team or check the DVLA guidance (gov.uk/driving-medical-conditions).",
  },
  {
    q: "How long will I need to take this medication?",
    a: "Rheumatic conditions are usually long-term, and many people stay on their medication for years. Some medications (like NSAIDs or steroids) may be used for short periods to manage flares. Others (like methotrexate or biologics) are usually ongoing treatments. Your rheumatology team will review your treatment regularly and adjust it based on how well your condition is controlled.",
  },
  {
    q: "What should I do if I miss a dose?",
    a: "If you miss a dose, take it as soon as you remember, unless your next dose is due soon — in that case, skip the missed dose and take your next one at the normal time. Do not double up on doses. If you are unsure what to do, contact your rheumatology advice line or check the medication leaflet that came with your prescription.",
  },
  {
    q: "I'm pregnant / planning pregnancy — what about my medication?",
    a: "Some rheumatology medications are safe in pregnancy, but others are not and may need to be changed or stopped before you conceive or during pregnancy. If you are sexually active and may become pregnant, speak to your rheumatology team about your medications — ideally 3-6 months before planning a pregnancy. Do not stop medication until you have discussed it with your team.",
  },
  {
    q: "Can I drink alcohol with my medication?",
    a: "Some rheumatology medications (especially methotrexate and leflunomide) affect the liver. Drinking alcohol while on these medications increases the risk of liver damage. For methotrexate, most guidelines recommend avoiding alcohol entirely. For other medications, moderate alcohol consumption may be acceptable — but check with your rheumatology team or pharmacist.",
  },
  {
    q: "What exercises are safe for my condition?",
    a: "Exercise is generally encouraged for most rheumatic conditions — it can help maintain joint mobility, reduce stiffness, and improve overall wellbeing. Gentle activities like walking, swimming, and specific exercises from a physiotherapist are often recommended. See the Exercises section on RheumHub for condition-specific exercise leaflets prepared by physiotherapists.",
  },
];

export default function AdviceFaq() {
  const [search, setSearch] = useState("");
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const filtered = useMemo(() => {
    if (!search.trim()) return FAQ_ITEMS;
    const q = search.toLowerCase();
    return FAQ_ITEMS.filter(
      (item) =>
        item.q.toLowerCase().includes(q) || item.a.toLowerCase().includes(q),
    );
  }, [search]);

  return (
    <PatientLayout title="Frequently Asked Questions">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search common questions..."
            className="pl-10 rounded-2xl h-12"
          />
        </div>

        {/* Intro */}
        <div className="bg-sky-50 border border-sky-200 rounded-2xl p-4 text-sm text-sky-800">
          <p className="leading-relaxed">
            Common questions people ask their rheumatology advice line. If you cannot find
            what you need, you can{" "}
            <Link href="/patient/requests" className="font-semibold underline text-sky-800 hover:text-sky-900">
              submit a request to your advice line
            </Link>{" "}
            or use the{" "}
            <Link href="/patient/chat" className="font-semibold underline text-sky-800 hover:text-sky-900">
              AI Guide
            </Link>{" "}
            for personalised information.
          </p>
        </div>

        {/* FAQ List */}
        <div className="space-y-2">
          {filtered.length === 0 ? (
            <p className="text-slate-500 text-center py-8">
              No results found for "{search}"
            </p>
          ) : (
            filtered.map((item, i) => {
              const idx = FAQ_ITEMS.indexOf(item);
              const isOpen = openIndex === idx;
              return (
                <div
                  key={idx}
                  className="border border-slate-200 rounded-2xl overflow-hidden transition-all"
                >
                  <button
                    onClick={() => setOpenIndex(isOpen ? null : idx)}
                    className="w-full text-left px-5 py-4 flex items-start justify-between gap-3 hover:bg-slate-50 transition-colors focus:outline-none focus:ring-2 focus:ring-sky-400 focus:ring-inset"
                  >
                    <span className="font-medium text-slate-900 text-sm leading-relaxed">
                      {item.q}
                    </span>
                    {isOpen ? (
                      <ChevronDown className="h-4 w-4 text-slate-400 shrink-0 mt-1" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-slate-400 shrink-0 mt-1" />
                    )}
                  </button>
                  {isOpen && (
                    <div className="px-5 pb-4">
                      <p className="text-sm text-slate-600 leading-relaxed">
                        {item.a}
                      </p>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Still need help CTA */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-3">
          <p className="font-semibold text-slate-900 text-sm">
            Still need help?
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              href="/patient/requests"
              className="inline-flex items-center justify-center gap-2 bg-rose-500 hover:bg-rose-600 text-white font-medium rounded-full px-5 py-2.5 text-sm transition-colors"
            >
              <FileText className="h-4 w-4" />
              Submit a request to your advice line
            </Link>
            <Link
              href="/patient/chat"
              className="inline-flex items-center justify-center gap-2 border border-slate-300 hover:bg-slate-50 text-slate-700 font-medium rounded-full px-5 py-2.5 text-sm transition-colors"
            >
              <MessageCircle className="h-4 w-4" />
              Ask the AI Guide
            </Link>
          </div>
        </div>

        {/* Urgent reminder */}
        <div className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3 text-sm text-amber-800 flex items-start gap-2">
          <p className="leading-relaxed">
            <strong>Urgent?</strong> If you need urgent medical help, call{" "}
            <a href="tel:999" className="font-semibold underline">999</a>.
            For urgent medical advice, call{" "}
            <a href="tel:111" className="font-semibold underline">NHS 111</a>.
          </p>
        </div>
      </div>
    </PatientLayout>
  );
}
